import React, { useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scale, Calculator, Info, TrendingUp, Building } from "lucide-react";

export const PPFvsELSSPage = () => {
  const [yearlyInvestment, setYearlyInvestment] = useState(150000);
  const [years, setYears] = useState(15);
  const [ppfRate, setPpfRate] = useState(7.1);
  const [elssReturn, setElssReturn] = useState(12);
  const [taxBracket, setTaxBracket] = useState(30);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const P = yearlyInvestment;
    const n = years;
    
    // PPF Calculation (compounded annually, contributions at year start)
    let ppfValue = 0;
    const ppfR = ppfRate / 100;
    for (let i = 0; i < n; i++) {
      ppfValue = (ppfValue + P) * (1 + ppfR);
    }
    
    // ELSS Calculation (same formula for consistency)
    let elssValue = 0;
    const elssR = elssReturn / 100;
    for (let i = 0; i < n; i++) {
      elssValue = (elssValue + P) * (1 + elssR);
    }
    
    const totalInvested = P * n;
    
    // Tax on ELSS gains (10% LTCG above 1L per year)
    const elssGains = elssValue - totalInvested;
    const exemptGains = Math.min(elssGains, 100000 * Math.ceil(n / 1)); // Simplified
    const taxableGains = Math.max(0, elssGains - exemptGains);
    const elssTax = taxableGains * 0.10; // 10% LTCG
    const elssPostTax = elssValue - elssTax;
    
    // Tax savings (both give 80C deduction)
    const taxSavings = totalInvested * (taxBracket / 100);
    
    setResult({
      totalInvested,
      ppfValue: Math.round(ppfValue),
      ppfGains: Math.round(ppfValue - totalInvested),
      elssPreTax: Math.round(elssValue),
      elssPostTax: Math.round(elssPostTax),
      elssGains: Math.round(elssGains),
      elssTax: Math.round(elssTax),
      taxSavings: Math.round(taxSavings),
      difference: Math.round(elssPostTax - ppfValue),
      ppfCAGR: ppfRate,
      elssCAGR: elssReturn
    });
  };

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              PPF vs <span className="gold-text">ELSS</span> Comparison
            </h1>
            <p className="text-muted-foreground">
              Compare tax-saving investment options for Section 80C
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Investment Parameters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label>Yearly Investment (₹)</Label>
                  <Input
                    type="number"
                    value={yearlyInvestment}
                    onChange={(e) => setYearlyInvestment(Number(e.target.value))}
                    max="150000"
                  />
                  <p className="text-xs text-muted-foreground">Max 80C limit: ₹1.5L</p>
                </div>

                <div className="space-y-2">
                  <Label>Investment Period (Years)</Label>
                  <Input
                    type="number"
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    min="3"
                    max="30"
                  />
                  <p className="text-xs text-muted-foreground">PPF: 15Y lock-in, ELSS: 3Y lock-in</p>
                </div>

                <div className="space-y-2">
                  <Label>PPF Interest Rate (% p.a.)</Label>
                  <Input
                    type="number"
                    value={ppfRate}
                    onChange={(e) => setPpfRate(Number(e.target.value))}
                    step="0.1"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected ELSS Return (% p.a.)</Label>
                  <Input
                    type="number"
                    value={elssReturn}
                    onChange={(e) => setElssReturn(Number(e.target.value))}
                    step="0.5"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Your Tax Bracket (%)</Label>
                  <select
                    value={taxBracket}
                    onChange={(e) => setTaxBracket(Number(e.target.value))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="0">0% (Below ₹2.5L)</option>
                    <option value="5">5% (₹2.5L - ₹5L)</option>
                    <option value="20">20% (₹5L - ₹10L)</option>
                    <option value="30">30% (Above ₹10L)</option>
                  </select>
                </div>

                <Button onClick={calculate} className="w-full">
                  <Scale className="w-4 h-4 mr-2" />
                  Compare
                </Button>
              </CardContent>
            </Card>

            {result && (
              <>
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="w-5 h-5 text-blue-500" />
                      PPF (Public Provident Fund)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 rounded-lg bg-blue-500/10">
                      <p className="text-sm text-muted-foreground">Maturity Value</p>
                      <p className="text-2xl font-bold text-blue-500">{formatCurrency(result.ppfValue)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Invested</p>
                        <p className="font-medium">{formatCurrency(result.totalInvested)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Interest Earned</p>
                        <p className="font-medium text-green-500">{formatCurrency(result.ppfGains)}</p>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-border text-sm space-y-1">
                      <p className="flex justify-between"><span>Lock-in:</span> <span>15 Years</span></p>
                      <p className="flex justify-between"><span>Risk:</span> <span className="text-green-500">Zero (Govt backed)</span></p>
                      <p className="flex justify-between"><span>Tax on Returns:</span> <span className="text-green-500">Nil (EEE)</span></p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      ELSS (Tax Saver Funds)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 rounded-lg bg-green-500/10">
                      <p className="text-sm text-muted-foreground">Post-Tax Value</p>
                      <p className="text-2xl font-bold text-green-500">{formatCurrency(result.elssPostTax)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Invested</p>
                        <p className="font-medium">{formatCurrency(result.totalInvested)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Gains</p>
                        <p className="font-medium text-green-500">{formatCurrency(result.elssGains)}</p>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-border text-sm space-y-1">
                      <p className="flex justify-between"><span>Lock-in:</span> <span>3 Years</span></p>
                      <p className="flex justify-between"><span>Risk:</span> <span className="text-orange-500">High (Market linked)</span></p>
                      <p className="flex justify-between"><span>LTCG Tax:</span> <span className="text-amber-500">{formatCurrency(result.elssTax)}</span></p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {result && (
            <Card className="mt-8 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-lg font-semibold">
                      {result.difference > 0 ? "ELSS" : "PPF"} wins by {formatCurrency(Math.abs(result.difference))}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Based on {years} years at {result.difference > 0 ? elssReturn : ppfRate}% returns
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total Tax Saved (80C)</p>
                    <p className="text-xl font-bold gold-text">{formatCurrency(result.taxSavings)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="mt-8 bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground space-y-2">
                  <p><strong>PPF:</strong> Safe, government-backed with guaranteed returns but 15-year lock-in.</p>
                  <p><strong>ELSS:</strong> Market-linked with higher return potential but comes with volatility. Only 3-year lock-in.</p>
                  <p><strong>Tip:</strong> Consider your risk appetite and investment horizon. A combination of both can balance safety and growth.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};
